import { TiptapJSON, ReportTemplate, TemplateSection } from '../types/template.types';

export interface SectionValidationResult {
  sectionId: string;
  sectionLabel: string;
  isValid: boolean;
  isEmpty: boolean;
  errors: string[];
}

export interface ReportValidationResult {
  isValid: boolean;
  sections: SectionValidationResult[];
  errors: string[];
}

/**
 * Check if a Tiptap JSON node is effectively empty
 * @param node - Tiptap JSON node to check
 * @returns True if node is empty or contains only whitespace
 */
function isNodeEmpty(node: TiptapJSON): boolean {
  if (!node) {
    return true;
  }

  // Check if it's a text node with content
  if (node.type === 'text') {
    const text = node.text || '';
    // Consider empty or only whitespace as empty
    return text.trim().length === 0;
  }

  // Check if it has content array
  if (!node.content || node.content.length === 0) {
    return true;
  }

  // Recursively check all child nodes
  return node.content.every((child) => isNodeEmpty(child as TiptapJSON));
}

/**
 * Check if section content contains only placeholder text
 * @param node - Tiptap JSON node to check
 * @param placeholderPatterns - Array of placeholder patterns to match
 * @returns True if node contains only placeholder text
 */
function hasOnlyPlaceholderText(node: TiptapJSON, placeholderPatterns: string[]): boolean {
  const text = extractTextContent(node);
  if (!text) {
    return true;
  }

  // Check if text matches any placeholder pattern
  return placeholderPatterns.some((pattern) => text.includes(pattern));
}

/**
 * Extract all text content from a Tiptap JSON node
 * @param node - Tiptap JSON node to extract text from
 * @returns Concatenated text content
 */
function extractTextContent(node: TiptapJSON): string {
  if (!node) {
    return '';
  }

  if (node.type === 'text') {
    return node.text || '';
  }

  if (!node.content) {
    return '';
  }

  return node.content.map((child) => extractTextContent(child as TiptapJSON)).join(' ');
}

/**
 * Find a section node by its section ID in Tiptap content
 * @param content - Tiptap JSON document content
 * @param sectionId - Section ID to find
 * @returns Found section node or null
 */
function findSectionNode(content: TiptapJSON, sectionId: string): TiptapJSON | null {
  if (!content) {
    return null;
  }

  // Check if current node is the section
  if (content.attrs?.sectionId === sectionId) {
    return content;
  }

  // Search in children
  if (content.content) {
    for (const child of content.content) {
      const found = findSectionNode(child as TiptapJSON, sectionId);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

/**
 * Validate a single section's content
 * @param section - Template section definition
 * @param content - Tiptap JSON document content
 * @returns Section validation result
 */
export function validateSection(
  section: TemplateSection,
  content: TiptapJSON
): SectionValidationResult {
  const sectionNode = findSectionNode(content, section.id);
  const errors: string[] = [];

  // Check if section exists
  if (!sectionNode) {
    return {
      sectionId: section.id,
      sectionLabel: section.label,
      isValid: !section.required,
      isEmpty: true,
      errors: section.required ? [`Η ενότητα "${section.label}" δεν βρέθηκε`] : [],
    };
  }

  // Check if section is empty
  const isEmpty = isNodeEmpty(sectionNode);

  // For non-editable sections, they're valid if they exist
  if (!section.editable) {
    return {
      sectionId: section.id,
      sectionLabel: section.label,
      isValid: true,
      isEmpty: false,
      errors: [],
    };
  }

  // Check if required section has content
  if (section.required && isEmpty) {
    errors.push(`Η ενότητα "${section.label}" είναι υποχρεωτική`);
  }

  // Check for placeholder-only content
  const placeholderPatterns = [
    'Εισάγετε',
    '{{',
    '_________________',
    'placeholder',
  ];

  if (!isEmpty && hasOnlyPlaceholderText(sectionNode, placeholderPatterns)) {
    if (section.required) {
      errors.push(`Η ενότητα "${section.label}" περιέχει μόνο placeholder κείμενο`);
    }
  }

  return {
    sectionId: section.id,
    sectionLabel: section.label,
    isValid: errors.length === 0,
    isEmpty,
    errors,
  };
}

/**
 * Validate all sections in a report
 * @param template - Report template with section definitions
 * @param content - Tiptap JSON document content
 * @returns Report validation result
 */
export function validateReport(
  template: ReportTemplate,
  content: TiptapJSON
): ReportValidationResult {
  const sectionResults = template.sections.map((section) =>
    validateSection(section, content)
  );

  const allErrors = sectionResults.flatMap((r) => r.errors);

  return {
    isValid: sectionResults.every((r) => r.isValid),
    sections: sectionResults,
    errors: allErrors,
  };
}

/**
 * Get list of required sections that are incomplete
 * @param template - Report template with section definitions
 * @param content - Tiptap JSON document content
 * @returns Array of incomplete template sections
 */
export function getIncompleteSections(
  template: ReportTemplate,
  content: TiptapJSON
): TemplateSection[] {
  const validation = validateReport(template, content);

  return template.sections.filter((section) => {
    const sectionValidation = validation.sections.find(
      (s) => s.sectionId === section.id
    );
    return sectionValidation && !sectionValidation.isValid;
  });
}

/**
 * Check if report has minimum required content for saving as draft
 * @param content - Tiptap JSON document content
 * @returns True if report can be saved as draft
 */
export function canSaveAsDraft(content: TiptapJSON): boolean {
  // Draft can be saved as long as there's any content
  return Boolean(content?.content && content.content.length > 0);
}

/**
 * Check if report has all required content for submission
 * @param template - Report template with section definitions
 * @param content - Tiptap JSON document content
 * @returns Object with canSubmit flag and missing requirements
 */
export function canSubmitForReview(
  template: ReportTemplate,
  content: TiptapJSON
): { canSubmit: boolean; missingRequirements: string[] } {
  const validation = validateReport(template, content);

  const missingRequirements = validation.sections
    .filter((s) => !s.isValid)
    .map((s) => s.sectionLabel);

  return {
    canSubmit: validation.isValid,
    missingRequirements,
  };
}

/**
 * Replace prefillable sections in saved content with fresh template versions.
 * This ensures dynamic data (e.g. patient info) is always up-to-date
 * even when loading a previously saved report.
 * @param savedContent - Previously saved Tiptap JSON document
 * @param template - The report template with fresh section definitions
 * @returns Tiptap JSON with prefillable sections replaced by template originals
 */
export function refreshPrefillableSections(
  savedContent: TiptapJSON,
  template: ReportTemplate
): TiptapJSON {
  const prefillableSectionIds = new Set(
    template.sections
      .filter((s) => s.prefillable)
      .map((s) => s.id)
  );

  if (prefillableSectionIds.size === 0 || !savedContent.content) {
    return savedContent;
  }

  // Build a map of fresh section content keyed by sectionId
  const freshSections = new Map<string, TiptapJSON>();
  for (const section of template.sections) {
    if (prefillableSectionIds.has(section.id)) {
      freshSections.set(section.id, section.content);
    }
  }

  return {
    ...savedContent,
    content: savedContent.content.map((node) => {
      const sectionId = node.attrs?.sectionId as string | undefined;
      const fresh = sectionId ? freshSections.get(sectionId) : undefined;
      return fresh ?? node;
    }),
  };
}
